import { useState, useCallback, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  deliveryApi,
  type DeliveryListParams,
  type Delivery,
  type DeliveryStatus,
  type DeliveryDriver,
  type DeliveryZone,
  type AssignDriverPayload,
} from "@/lib/admin-api/delivery";

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

const DELIVERIES_KEY = "admin-deliveries";

function deliveriesQueryKey(params: DeliveryListParams) {
  return [DELIVERIES_KEY, params] as const;
}

// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------

export const KANBAN_COLUMNS: readonly {
  readonly id: DeliveryStatus;
  readonly labelKey: string;
}[] = [
  { id: "unassigned", labelKey: "delivery_board.columns.unassigned" },
  { id: "assigned", labelKey: "delivery_board.columns.assigned" },
  { id: "in_transit", labelKey: "delivery_board.columns.in_transit" },
  { id: "delivered", labelKey: "delivery_board.columns.delivered" },
] as const;

// ---------------------------------------------------------------------------
// Filter state
// ---------------------------------------------------------------------------

export interface DeliveryFilters {
  readonly zone_id: string | null;
  readonly date_from: string | null;
  readonly date_to: string | null;
}

const INITIAL_FILTERS: DeliveryFilters = {
  zone_id: null,
  date_from: null,
  date_to: null,
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useDeliveryBoard() {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<DeliveryFilters>(INITIAL_FILTERS);
  const [viewMode, setViewMode] = useState<"kanban" | "map">("kanban");

  // Build query params from filter state
  const params: DeliveryListParams = useMemo(
    () => ({
      limit: 200,
      zone_id: filters.zone_id,
      date_from: filters.date_from,
      date_to: filters.date_to,
    }),
    [filters],
  );

  // Fetch all deliveries
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: deliveriesQueryKey(params),
    queryFn: () => deliveryApi.list(params),
    staleTime: 15_000,
    refetchInterval: 30_000,
    placeholderData: (prev) => prev,
  });

  // Group deliveries by status for Kanban columns
  const columns = useMemo(() => {
    const deliveries = data?.deliveries ?? [];
    const grouped: Record<DeliveryStatus, readonly Delivery[]> = {
      unassigned: [],
      assigned: [],
      in_transit: [],
      delivered: [],
    };
    for (const delivery of deliveries) {
      const status = delivery.status as DeliveryStatus;
      if (status in grouped) {
        grouped[status] = [...grouped[status], delivery];
      } else {
        // Default unknown statuses to unassigned
        grouped.unassigned = [...grouped.unassigned, delivery];
      }
    }
    return grouped;
  }, [data?.deliveries]);

  // Available drivers and zones from the API response
  const drivers: readonly DeliveryDriver[] = data?.drivers ?? [];
  const zones: readonly DeliveryZone[] = data?.zones ?? [];

  // Assign driver mutation
  const assignMutation = useMutation({
    mutationFn: ({
      deliveryId,
      payload,
    }: {
      deliveryId: string;
      payload: AssignDriverPayload;
    }) => deliveryApi.assign(deliveryId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DELIVERIES_KEY] });
    },
  });

  // Filter change handler — immutable update
  const updateFilter = useCallback(
    (key: string, value: unknown) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const resetFilters = useCallback(() => {
    setFilters(INITIAL_FILTERS);
  }, []);

  // Deliveries with coordinates for the map view
  const mapDeliveries = useMemo(() => {
    const deliveries = data?.deliveries ?? [];
    return deliveries.filter(
      (d): d is Delivery & { latitude: number; longitude: number } =>
        d.latitude != null &&
        d.longitude != null &&
        d.status !== "delivered",
    );
  }, [data?.deliveries]);

  return {
    // Data
    columns,
    drivers,
    zones,
    mapDeliveries,
    totalCount: data?.pagination.total ?? 0,

    // Loading / error
    isLoading,
    isError,
    error,
    refetch,

    // Filters
    filters,
    updateFilter,
    resetFilters,

    // View mode
    viewMode,
    setViewMode,

    // Mutations
    assignDriver: assignMutation.mutateAsync,
    isAssigning: assignMutation.isPending,
  } as const;
}
